import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Heart, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { API, useAuth } from '../App';

const FoundingVisionaryWelcome = ({ onContinue }) => {
  const { token } = useAuth();
  const [isVisible, setIsVisible] = useState(true);

  const handleContinue = async () => {
    try {
      // Mark welcome as seen
      await axios.post(`${API}/user/founding-welcome-seen`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error marking welcome as seen:', error);
    }
    
    setIsVisible(false);
    setTimeout(() => {
      onContinue?.();
    }, 500);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-[#050505]/95 backdrop-blur-sm"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="max-w-2xl mx-4 text-center"
        >
          {/* Decorative elements */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mb-8"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 to-transparent">
              <Sparkles className="text-[#D4AF37]" size={36} />
            </div>
          </motion.div>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-6"
          >
            <span className="inline-block px-6 py-2 bg-gradient-to-r from-[#D4AF37]/20 via-[#E5C585]/10 to-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-accent tracking-[0.25em]">
              ✨ FOUNDING VISIONARY ✨
            </span>
          </motion.div>

          {/* Main title */}
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="font-serif text-4xl md:text-5xl text-white mb-6"
          >
            Bienvenue, Founding Visionary.
          </motion.h1>

          {/* Message */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="space-y-4 mb-10"
          >
            <p className="text-[#A1A1AA] text-lg leading-relaxed">
              Merci d'avoir cru en Clierxter Vision Lab dès le début.
            </p>
            <p className="text-[#A1A1AA] leading-relaxed">
              Ta confiance, tes retours et ton soutien ont contribué à façonner cette plateforme.
            </p>
            <p className="text-[#A1A1AA] leading-relaxed">
              En signe de gratitude, ton compte inclura toujours ta 
              <span className="text-[#D4AF37]"> réduction de 20% à vie </span>
              et ton badge exclusif 
              <span className="text-[#D4AF37]"> Founding Visionary</span>.
            </p>
          </motion.div>

          {/* Benefits summary */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-[#0A0A0A] border border-[#1C1C1C] p-6 mb-10 text-left"
          >
            <h3 className="text-[#D4AF37] text-sm font-accent tracking-wider mb-4">TES AVANTAGES PERMANENTS</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-[#A1A1AA] text-sm">
                <Sparkles className="text-[#D4AF37] flex-shrink-0" size={14} />
                Badge permanent ✨ Founding Visionary
              </li>
              <li className="flex items-center gap-3 text-[#A1A1AA] text-sm">
                <Sparkles className="text-[#D4AF37] flex-shrink-0" size={14} />
                20% de réduction à vie sur tous les abonnements
              </li>
              <li className="flex items-center gap-3 text-[#A1A1AA] text-sm">
                <Sparkles className="text-[#D4AF37] flex-shrink-0" size={14} />
                Réduction appliquée automatiquement
              </li>
              <li className="flex items-center gap-3 text-[#A1A1AA] text-sm">
                <Sparkles className="text-[#D4AF37] flex-shrink-0" size={14} />
                Statut préservé même après annulation
              </li>
            </ul>
          </motion.div>

          {/* Closing message */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="text-[#71717A] text-sm italic mb-8"
          >
            Nous sommes honorées de t'avoir parmi les femmes qui ont contribué à bâtir cette vision.
          </motion.p>

          {/* Continue button */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <Button
              onClick={handleContinue}
              className="btn-gold px-12 py-4"
              data-testid="founding-welcome-continue"
            >
              Continuer
              <ArrowRight className="ml-2" size={16} />
            </Button>
          </motion.div>

          {/* Heart decoration */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="mt-8"
          >
            <Heart className="inline-block text-[#D4AF37]/30" size={16} />
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FoundingVisionaryWelcome;
